import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, X, AlertCircle, Server } from 'lucide-react';
import { getNetworkStatus, setupNetworkListeners, isLowQualityConnection } from '../../services/domain/networkService';
import { useSupabase } from '../../contexts/SupabaseContext';
import { checkApiHealth, getCachedApiHealth } from '../../services/healthService';

const NetworkStatus: React.FC = () => {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [visible, setVisible] = useState<boolean>(!navigator.onLine);
  const [apiStatus, setApiStatus] = useState<'healthy' | 'degraded' | 'offline'>('healthy');
  const { isSupabaseAvailable } = useSupabase();
  
  useEffect(() => {
    // Handle online status change
    const handleOnline = () => {
      setIsOnline(true);
      setVisible(true);
      
      // Check API health when coming back online
      checkApiHealth()
        .then(health => {
          setApiStatus(health.status);
          
          // Auto-hide notification after delay if everything is working well
          if (health.status === 'healthy' && isSupabaseAvailable) {
            setTimeout(() => setVisible(false), 3000);
          }
        });
    };

    // Handle offline status change
    const handleOffline = () => {
      setIsOnline(false);
      setVisible(true);
    };
    
    // Set up network event listeners using our service
    const cleanup = setupNetworkListeners(handleOnline, handleOffline);
    
    // Initial API health check
    if (navigator.onLine) {
      // Check if we have a cached health status first
      const cachedHealth = getCachedApiHealth();
      if (cachedHealth) {
        setApiStatus(cachedHealth.status);
      }
      
      // Then fetch the current status
      checkApiHealth()
        .then(health => setApiStatus(health.status));
    }
    
    // Check connection quality
    if (isLowQualityConnection()) {
      setVisible(true);
    }

    // Set up interval to check API health every 30 seconds when online
    const intervalId = setInterval(() => {
      if (navigator.onLine) {
        checkApiHealth()
          .then(health => {
            const previousStatus = apiStatus;
            setApiStatus(health.status);
            
            // Show notification if status changed
            if (previousStatus !== health.status) {
              setVisible(true);
              
              // Auto-hide after delay if healthy
              if (health.status === 'healthy') {
                setTimeout(() => setVisible(false), 3000);
              }
            }
          });
      }
    }, 30000);
    
    // Cleanup function
    return () => {
      cleanup(); // Remove network listeners
      clearInterval(intervalId);
    };
  }, [apiStatus, isSupabaseAvailable]);

  if (!visible) return null;

  // Status message based on connectivity
  let statusMessage = '';
  let statusColor = '';
  
  if (!isOnline) {
    statusMessage = 'You are offline. Showing previously loaded content.';
    statusColor = 'bg-yellow-50 text-yellow-700';
  } else if (apiStatus === 'offline') {
    statusMessage = 'Cannot connect to Legal Nest API. Using cached content.';
    statusColor = 'bg-red-50 text-red-700';
  } else if (apiStatus === 'degraded') {
    statusMessage = 'Legal Nest API is experiencing issues. Some features may be limited.';
    statusColor = 'bg-orange-50 text-orange-700';
  } else if (!isSupabaseAvailable) {
    statusMessage = 'Cannot connect to Legal Nest database. Using cached content.';
    statusColor = 'bg-yellow-50 text-yellow-700';
  } else {
    statusMessage = 'Connected to Legal Nest services. Content has been cached for offline use.';
    statusColor = 'bg-green-50 text-green-700';
  }

  // Status icon based on connectivity
  const StatusIcon = () => {
    if (!isOnline) {
      return <WifiOff className="mr-2" size={18} />;
    } else if (apiStatus === 'offline' || apiStatus === 'degraded') {
      return <Server className="mr-2" size={18} />;
    } else if (!isSupabaseAvailable) {
      return <AlertCircle className="mr-2" size={18} />;
    } else {
      return <Wifi className="mr-2" size={18} />;
    }
  };

  return (
    <div className={`fixed bottom-4 left-4 right-4 z-50 p-3 rounded-md shadow-lg flex items-center justify-between ${statusColor}`}>
      <div className="flex items-center">
        <StatusIcon />
        <span className="text-sm">{statusMessage}</span>
      </div>
      <button 
        onClick={() => setVisible(false)}
        className="text-sm font-medium flex items-center"
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default NetworkStatus;